import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, onboardingAPI } from '../api';
import { LANGUAGE_OPTIONS, getTranslation } from '../i18n';
import StorageWarningBanner from '../components/StorageWarningBanner';
import ContactMaestraModal from '../components/ContactMaestraModal';
import PlatformFooter from '../components/PlatformFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserSettings = () => {
  const navigate = useNavigate();
  document.title = 'Maestra - Settings';

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [branding, setBranding] = useState(null);
  const [toast, setToast] = useState(null);

  // i18n: workspace language for the user. Loaded from settings.
  const [workspaceLang, setWorkspaceLang] = useState('en');
  const t = getTranslation(workspaceLang);

  // Branding form state
  const [brandName, setBrandName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [savingBrandName, setSavingBrandName] = useState(false);
  const fileInputRef = useRef(null);


  // Support access
  const [supportEnabled, setSupportEnabled] = useState(false);
  const [activeModules, setActiveModules] = useState(['boards', 'client_space']);
  const [contactOpen, setContactOpen] = useState(false);
  const [supportExpires, setSupportExpires] = useState(null);
  const [togglingSA, setTogglingSA] = useState(false);

  // Language
  const [language, setLanguage] = useState('en');


  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, usageRes, brandingRes] = await Promise.all([
        authAPI.getSettings(),
        authAPI.getUsage(),
        authAPI.getBranding(),
      ]);
      setSettings(settingsRes.data);
      setUsage(usageRes.data);

      const b = brandingRes.data;
      setBranding(b);
      setBrandName(b.overrides?.workspace_name || b.resolved?.workspace_name || '');
      setLogoUrl(b.resolved?.workspace_logo_url || '');

      setSupportEnabled(settingsRes.data.support_access_enabled || false);
      setActiveModules(settingsRes.data.active_modules || ['boards', 'client_space']);
      setSupportExpires(settingsRes.data.support_access_expires_at || null);
      setLanguage(settingsRes.data.language || 'en');
      setWorkspaceLang(settingsRes.data.language || 'en');
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await authAPI.updateSettings({ language: lang });
      showToast('Language updated');
    } catch (err) {
      console.error('Error saving language:', err);
    }
  };

  const formatStorage = (mb) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(mb * 1024).toFixed(0)} KB`;
  };

  const handleModuleToggle = async (key) => {
    const next = activeModules.includes(key)
      ? activeModules.filter((m) => m !== key)
      : [...activeModules, key];
    setActiveModules(next);
    try {
      await authAPI.updateSettings({ active_modules: next });
      showToast(t.cs_saved || 'Saved');
    } catch (err) {
      setActiveModules(activeModules);
      showToast(t.save_failed || 'Could not save');
    }
  };

  // Support access toggle
  const handleToggleSupport = async () => {
    setTogglingSA(true);
    try {
      const { data } = await authAPI.toggleSupportAccess(!supportEnabled);
      setSupportEnabled(data.support_access_enabled);
      setSupportExpires(data.support_access_expires_at);
      showToast(data.support_access_enabled ? 'Support access enabled for 24 hours' : 'Support access disabled');
    } catch (err) {
      console.error('Error toggling support access:', err);
    } finally {
      setTogglingSA(false);
    }
  };

  const getExpiresIn = () => {
    if (!supportExpires) return null;
    const diff = new Date(supportExpires) - new Date();
    if (diff <= 0) return null;
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  // Branding save
  // Brand name save — always available, not gated by can_customize_branding
  const handleSaveBrandName = async () => {
    if (!brandName.trim()) {
      showToast('Brand name cannot be empty');
      return;
    }
    setSavingBrandName(true);
    try {
      await authAPI.updateBranding({ workspace_name: brandName.trim() });
      await fetchAll();
      showToast('Brand name saved');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save brand name');
    } finally {
      setSavingBrandName(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await authAPI.uploadBrandingLogo(file);
      setLogoUrl(data.resolved?.workspace_logo_url || '');
      showToast('Logo updated');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Upload failed');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteLogo = async () => {
    try {
      await authAPI.deleteBrandingLogo();
      setLogoUrl('');
      showToast('Logo removed');
    } catch (err) {
      showToast('Error removing logo');
    }
  };

  const canBrand = settings?.can_customize_branding;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '120px' }}>
        <div className="spinner" />
      </div>
    );
  }

  const sectionStyle = {
    background: 'var(--kahla-white)',
    border: '1px solid var(--kahla-border)',
    padding: '28px 32px',
    marginBottom: '20px',
  };
  const sectionTitle = {
    fontSize: '10px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--kahla-text-light)',
    marginBottom: '20px',
    fontWeight: '600',
  };
  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--kahla-border)',
  };
  const labelStyle = { fontSize: '14px', color: 'var(--kahla-text)' };
  const valueStyle = { fontSize: '14px', color: 'var(--kahla-charcoal)', fontWeight: '500' };

  return (
    <div className="fade-in user-settings-wrap">
      {/* Header */}
      <div className="settings-narrow" style={{ marginBottom: '40px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          data-testid="settings-back-btn"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '13px', color: 'var(--kahla-text-light)',
            padding: 0, marginBottom: '16px', fontFamily: 'inherit',
          }}
        >
          {t.back_to_workspace}
        </button>
        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '28px',
          color: 'var(--kahla-charcoal)',
        }}>
          {t.settings_title || t.settings}
        </h1>
      </div>

      {/* 1. ACCOUNT */}
      <div style={sectionStyle} className="settings-narrow" data-testid="settings-account-section">
        <p style={sectionTitle}>{t.account_section}</p>
        <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <span style={labelStyle}>{t.brand_name_label}</span>
          <input
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            data-testid="settings-workspace-name-input"
            placeholder={t.brand_name_placeholder}
            style={{
              padding: '10px 14px', fontSize: '14px',
              border: '1px solid var(--kahla-border)',
              fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
            <button
              className="btn-secondary"
              onClick={handleSaveBrandName}
              disabled={savingBrandName || brandName === (settings?.branding?.workspace_name || '')}
              data-testid="settings-save-brand-name"
              style={{ fontSize: '12px', padding: '6px 16px' }}
            >
              {savingBrandName ? t.saving_short : t.save_brand_name}
            </button>
          </div>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t.email_label}</span>
          <span style={{ ...valueStyle, color: settings?.email ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)' }} data-testid="settings-email">
            {settings?.email || '—'}
          </span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t.username_label}</span>
          <span style={valueStyle} data-testid="settings-username">{settings?.username}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t.status_label}</span>
          <span
            data-testid="settings-status"
            style={{
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.05em',
              textTransform: 'capitalize',
              padding: '4px 12px',
              backgroundColor:
                settings?.status === 'active' ? '#ecfdf5' :
                settings?.status === 'invited' ? '#fffbeb' : '#fef2f2',
              color:
                settings?.status === 'active' ? '#065f46' :
                settings?.status === 'invited' ? '#92400e' : '#991b1b',
            }}
          >
            {settings?.status === 'active' ? t.status_active_pill : (settings?.status || t.status_active_pill)}
          </span>
        </div>
        <div style={{ ...rowStyle, borderBottom: 'none' }}>
          <span style={labelStyle}>{t.access_label}</span>
          <AccessPeriodDisplay settings={settings} t={t} />
        </div>
      </div>

      {/* 2. USAGE */}
      {usage && (
        <div style={sectionStyle} className="settings-narrow" data-testid="settings-usage-section">
          <p style={sectionTitle}>{t.usage_section}</p>
          <StorageWarningBanner usage={usage} compact onDismiss={false} />
          <div style={rowStyle}>
            <span style={labelStyle}>{t.storage_label}</span>
            <span style={valueStyle} data-testid="settings-storage">
              {formatStorage(usage.storage_used_mb)}
              {usage.storage_limit_mb > 0 && (
                <span style={{ fontWeight: '400', color: 'var(--kahla-text-light)' }}>
                  {' '}/ {formatStorage(usage.storage_limit_mb)}
                </span>
              )}
              {!usage.storage_limit_mb && (
                <span style={{ fontWeight: '400', color: 'var(--kahla-text-light)' }}> / {t.unlimited || 'Unlimited'}</span>
              )}
            </span>
          </div>
          {usage.storage_limit_mb > 0 && (() => {
            const pct = Math.min(100, (usage.storage_used_mb / usage.storage_limit_mb) * 100);
            const barColor = pct >= 100 ? '#b35a4a' : pct >= 90 ? '#cf9a3d' : pct >= 80 ? '#e5c97a' : '#8B7355';
            return (
              <div style={{ height: '4px', background: '#e8e4de', borderRadius: '2px', margin: '0 0 12px' }}>
                <div style={{
                  height: '100%', borderRadius: '2px', transition: 'width 0.5s, background-color 0.3s',
                  width: `${pct}%`,
                  backgroundColor: barColor,
                }} />
              </div>
            );
          })()}
          <div style={rowStyle}>
            <span style={labelStyle}>{t.boards_label}</span>
            <span style={valueStyle} data-testid="settings-boards">
              {usage.board_count}
              {usage.max_boards != null ? (
                <span style={{ fontWeight: '400', color: 'var(--kahla-text-light)' }}> / {usage.max_boards}</span>
              ) : (
                <span style={{ fontWeight: '400', color: 'var(--kahla-text-light)' }}> / {t.unlimited || 'Unlimited'}</span>
              )}
            </span>
          </div>
          <div style={{ ...rowStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>{t.images_in_library}</span>
            <span style={valueStyle} data-testid="settings-images">{usage.library_image_count}</span>
          </div>
        </div>
      )}

      {/* 3. LANGUAGE */}
      <div style={sectionStyle} className="settings-narrow" data-testid="settings-language-section">
        <p style={sectionTitle}>{t.language_section}</p>
        <div style={{ ...rowStyle, borderBottom: 'none' }}>
          <span style={labelStyle}>{t.workspace_language}</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            {LANGUAGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => handleLanguageChange(opt.value)}
                data-testid={`lang-btn-${opt.value}`}
                style={{
                  padding: '8px 18px', fontSize: '13px', fontWeight: '500',
                  fontFamily: 'inherit', cursor: 'pointer',
                  border: language === opt.value ? '1px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
                  backgroundColor: language === opt.value ? 'var(--kahla-charcoal)' : 'transparent',
                  color: language === opt.value ? 'white' : 'var(--kahla-text)',
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 3b. MODULES — which parts of Maestra are visible */}
      <div style={sectionStyle} className="settings-narrow" data-testid="settings-modules-section">
        <p style={sectionTitle}>{t.modules_section || 'My modules'}</p>
        {[
          { key: 'boards', label: t.wd_boards || 'Boards' },
          { key: 'client_space', label: t.wd_clients || 'Client Space' },
          { key: 'creator_space', label: t.wd_creator || 'Creator Space' },
        ].map(({ key, label }) => {
          const on = activeModules.includes(key);
          return (
            <div key={key} style={rowStyle}>
              <span style={labelStyle}>{label}</span>
              <button
                type="button"
                data-testid={`module-toggle-${key}`}
                onClick={() => handleModuleToggle(key)}
                style={{
                  padding: '8px 18px', fontSize: '13px', fontWeight: '500',
                  fontFamily: 'inherit', cursor: 'pointer',
                  border: on ? '1px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
                  backgroundColor: on ? 'var(--kahla-charcoal)' : 'transparent',
                  color: on ? 'white' : 'var(--kahla-text)',
                  transition: 'all 0.15s',
                }}
              >
                {on ? (t.module_on || 'On') : (t.module_off || 'Off')}
              </button>
            </div>
          );
        })}
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 14, color: 'var(--kahla-text-light)', margin: '14px 0 0', lineHeight: 1.6,
        }}>{t.modules_note || 'Modules you switch off disappear from your workspace entirely. Library is part of the core and always stays.'}</p>
      </div>

      {/* 4. BRAND IDENTITY — workspace-level identity */}
      {canBrand && (
        <div style={sectionStyle} className="settings-narrow" data-testid="settings-branding-section">
          <p style={sectionTitle}>{t.card_brand_identity}</p>
          <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', margin: '0 0 20px', lineHeight: 1.7, fontStyle: 'italic', fontFamily: 'Cormorant Garamond, Playfair Display, serif' }}>
            {t.card_brand_identity_desc}
          </p>
        {/* Logo */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <label style={{ fontSize: '11px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--kahla-text-light)' }}>{t.logo_label}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={handleLogoUpload}
            style={{ display: 'none' }}
            data-testid="settings-logo-input"
          />

          {logoUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Large premium preview tile — neutral checker to reveal transparency */}
              <div
                data-testid="settings-logo-preview-tile"
                style={{
                  width: '100%',
                  height: '180px',
                  border: '1px solid var(--kahla-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                  boxSizing: 'border-box',
                  background:
                    'repeating-conic-gradient(#f5f1ea 0% 25%, #faf8f5 0% 50%) 50% / 18px 18px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}/api/images/${logoUrl}`}
                  alt="Logo"
                  data-testid="settings-logo-preview"
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary"
                  style={{ fontSize: '12px', padding: '8px 18px' }}
                  data-testid="settings-upload-logo-btn"
                >
                  {t.change || 'Change'}
                </button>
                <button
                  onClick={handleDeleteLogo}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '11px', color: 'var(--kahla-text-light)',
                    padding: '8px 4px', textDecoration: 'underline',
                    textUnderlineOffset: '3px', letterSpacing: '0.04em',
                  }}
                  data-testid="settings-remove-logo-btn"
                >
                  {t.remove || 'Remove'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="settings-upload-logo-btn"
              style={{
                width: '100%',
                height: '180px',
                border: '1px dashed var(--kahla-border)',
                background: '#faf8f5',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--kahla-text-light)';
                e.currentTarget.style.background = '#f7f3ec';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--kahla-border)';
                e.currentTarget.style.background = '#faf8f5';
              }}
            >
              <span style={{
                fontSize: '13px',
                letterSpacing: '0.06em',
                color: 'var(--kahla-text)',
                fontFamily: 'Playfair Display, serif',
              }}>
                {t.upload_logo_cta || t.upload_logo}
              </span>
              <span style={{
                fontSize: '10px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text-light)',
              }}>
                {t.upload_logo_hint || 'PNG · SVG · JPG'}
              </span>
            </button>
          )}

          {/* Editorial helper texts */}
          <div
            data-testid="settings-logo-helper"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              marginTop: '2px',
            }}
          >
            <p style={{
              fontSize: '11px',
              color: 'var(--kahla-text-light)',
              margin: 0,
              lineHeight: 1.6,
              fontStyle: 'italic',
            }}>
              {t.logo_helper_primary}
            </p>
            <p style={{
              fontSize: '10px',
              color: 'var(--kahla-text-light)',
              margin: 0,
              lineHeight: 1.6,
              opacity: 0.75,
            }}>
              {t.logo_helper_secondary}
            </p>
          </div>
        </div>

          <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--kahla-border)', display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="settings-board-settings-hint">
            <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.7, fontStyle: 'italic', fontFamily: 'Cormorant Garamond, Playfair Display, serif' }}>
              {t.workspace_board_settings_hint}
            </p>
            <button
              type="button"
              data-testid="settings-open-board-settings-link"
              onClick={() => navigate('/boards/settings')}
              style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', letterSpacing: '0.06em', color: 'var(--kahla-charcoal)', textDecoration: 'underline', textUnderlineOffset: '4px' }}
            >
              {t.open_board_settings} →
            </button>
          </div>
        </div>
      )}

      {/* 4. SUPPORT ACCESS */}
      <div style={sectionStyle} className="settings-narrow" data-testid="settings-support-section">
        <p style={sectionTitle}>{t.support_access}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: '14px', color: 'var(--kahla-text)', marginBottom: '4px' }}>
              {t.support_access_desc || 'Allow admin support access'}
            </p>
            <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)' }}>
              {supportEnabled && getExpiresIn()
                ? `${t.support_access_active_expires_in || 'Access active — expires in'} ${getExpiresIn()}`
                : (t.support_access_disabled || 'Disabled')}
            </p>
          </div>
          <button
            onClick={handleToggleSupport}
            disabled={togglingSA}
            data-testid="settings-support-toggle"
            style={{
              width: '48px', height: '26px', borderRadius: '13px', border: 'none',
              cursor: togglingSA ? 'wait' : 'pointer',
              backgroundColor: supportEnabled && getExpiresIn() ? '#4a7c59' : '#d1d5db',
              position: 'relative', transition: 'background-color 0.2s',
            }}
          >
            <span style={{
              position: 'absolute', top: '3px',
              left: supportEnabled && getExpiresIn() ? '24px' : '3px',
              width: '20px', height: '20px', borderRadius: '50%',
              backgroundColor: 'white',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
      </div>

      {/* 5. STUDIO GUIDE & DEMO RESTORE */}
      <div style={sectionStyle} className="settings-narrow" data-testid="settings-guide-section">
        <p style={sectionTitle}>{t.guide_nav_label}</p>
        <p style={{ fontSize: '13px', color: 'var(--kahla-text-light)', margin: '0 0 16px', lineHeight: 1.7, fontStyle: 'italic', fontFamily: 'Cormorant Garamond, Playfair Display, serif' }}>
          {t.guide_intro}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => navigate('/guide')}
            data-testid="settings-open-guide-btn"
            className="btn-secondary"
            style={{ fontSize: '12px' }}
          >
            {t.guide_nav_label}
          </button>

          <RestoreDemoBoardBlock t={t} />
          <ReplaySpotlightBlock t={t} />
        </div>
      </div>

      {/* 6. NEED HELP? — founder-led contact flow */}
      <div style={sectionStyle} className="settings-narrow" data-testid="settings-need-help-section">
        <p style={sectionTitle}>{t.need_help_section || 'Need help?'}</p>
        <p style={{
          fontSize: '15px',
          color: 'var(--kahla-text-light)',
          margin: '0 0 22px',
          lineHeight: 1.65,
          fontStyle: 'italic',
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          maxWidth: 560,
        }}>
          {t.need_help_body}
        </p>
        <button
          type="button"
          onClick={() => setContactOpen(true)}
          data-testid="contact-maestra-btn"
          className="btn-secondary"
          style={{ fontSize: '12px' }}
        >
          {t.contact_maestra_btn || 'Contact Maestra'}
        </button>
      </div>

      {/* Contact Maestra modal */}
      <ContactMaestraModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        t={t}
      />

      {/* Footer */}
      <footer className="settings-narrow" style={{
        padding: '32px 0', textAlign: 'center', borderTop: '1px solid var(--kahla-border)', marginTop: '28px',
      }}>
        <PlatformFooter />
      </footer>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

// Subtle "Restore Demo Board" block — only visible if the user has used
// the restore allowance. Stays calm & secondary; never aggressively recreates.
const RestoreDemoBoardBlock = ({ t }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    onboardingAPI.state()
      .then(res => setState(res.data))
      .catch(() => {});
  }, []);

  if (!state) return null;
  // Only show if there's no demo board AND the user hasn't exhausted restores
  if (state.has_demo_board) return null;

  const remaining = (state.demo_max_restores || 3) - (state.demo_restore_count || 0);
  if (remaining <= 0) return null;

  const restore = async () => {
    setBusy(true);
    try {
      await onboardingAPI.restoreDemo();
      setMessage(t.restore_demo_done || 'Demo board restored.');
      // refresh state
      const res = await onboardingAPI.state();
      setState(res.data);
    } catch (e) {
      setMessage(e?.response?.data?.detail || t.restore_demo_limit);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="settings-restore-demo-block"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
      }}
    >
      <button
        type="button"
        onClick={restore}
        disabled={busy}
        data-testid="settings-restore-demo-btn"
        style={{
          background: 'none',
          border: 'none',
          padding: '4px 0',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 12,
          letterSpacing: '0.04em',
          color: 'var(--kahla-charcoal)',
          textDecoration: 'underline',
          textUnderlineOffset: 4,
        }}
      >
        {busy ? '…' : (t.restore_demo_cta || 'Restore demo board')}
      </button>
      {message && (
        <span style={{
          fontSize: 11,
          fontStyle: 'italic',
          color: 'var(--kahla-text-light)',
        }}>
          {message}
        </span>
      )}
    </div>
  );
};

// Subtle "Replay onboarding" link — re-arms the demo board spotlight tour.
// Visible only when a demo board exists in the user's workspace.
const ReplaySpotlightBlock = ({ t = {} }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    onboardingAPI.state()
      .then((res) => setState(res.data))
      .catch(() => {});
  }, []);

  if (!state) return null;
  if (!state.has_demo_board || !state.demo_board?.id) return null;

  const replay = async () => {
    setBusy(true);
    try {
      await onboardingAPI.replayGuidance('demo_spotlight');
      navigate(`/editor/${state.demo_board.id}`);
    } catch (e) {
      // non-fatal; let user retry
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={replay}
      disabled={busy}
      data-testid="settings-replay-spotlight-btn"
      style={{
        background: 'none',
        border: 'none',
        padding: '4px 0',
        cursor: busy ? 'wait' : 'pointer',
        fontFamily: 'inherit',
        fontSize: 12,
        letterSpacing: '0.04em',
        color: 'var(--kahla-text-light)',
        textDecoration: 'underline',
        textUnderlineOffset: 3,
        textDecorationColor: 'rgba(166,152,136,0.45)',
        opacity: busy ? 0.6 : 1,
      }}
    >
      {t.replay_onboarding || 'Replay onboarding'}
    </button>
  );
};

const AccessPeriodDisplay = ({ settings, t = {} }) => {
  if (!settings) return null;

  const accessStatus = settings.access_status;
  const role = settings.role;

  if (role === 'admin') {
    return (
      <div data-testid="settings-access" style={{ textAlign: 'right' }}>
        <span style={{ fontSize: '14px', color: 'var(--kahla-charcoal)', fontWeight: '500' }}>{t.unlimited_access || 'Unlimited access'}</span>
      </div>
    );
  }

  const endDate = accessStatus === 'trial' ? settings.trial_ends_at : settings.access_expires_at;

  if (!endDate) {
    return (
      <div data-testid="settings-access" style={{ textAlign: 'right' }}>
        <span style={{ fontSize: '14px', color: 'var(--kahla-charcoal)', fontWeight: '500' }}>{t.unlimited_access || 'Unlimited access'}</span>
      </div>
    );
  }

  const now = new Date();
  const end = new Date(endDate);
  const diffMs = end - now;
  const remainingDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let text;
  let textColor = 'var(--kahla-charcoal)';
  const isExpired = accessStatus === 'expired_read_only' || accessStatus === 'archived_frozen' || remainingDays < 0;
  const isEndingSoon = !isExpired && remainingDays <= 7 && remainingDays >= 0;
  const showExtendButton = role !== 'admin' && (isEndingSoon || isExpired || (endDate && remainingDays <= 30));

  if (isExpired) {
    text = t.access_expired || 'Access expired';
    textColor = '#991b1b';
  } else if (remainingDays === 0) {
    text = t.expires_today || 'Expires today';
    textColor = '#92400e';
  } else if (remainingDays === 1) {
    text = `1 ${t.day_remaining || 'day remaining'}`;
    textColor = '#92400e';
  } else {
    text = `${remainingDays} ${t.days_remaining || 'days remaining'}`;
  }

  const localeStr = t.locale || 'en-GB';
  const formattedDate = end.toLocaleDateString(localeStr, { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div data-testid="settings-access" style={{ textAlign: 'right' }}>
      <span style={{ fontSize: '14px', color: textColor, fontWeight: '500' }}>{text}</span>
      {!isExpired && remainingDays >= 0 && (
        <div style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '2px' }}>
          {t.access_valid_until || 'Access valid until'} {formattedDate}
        </div>
      )}
      {isEndingSoon && (
        <div data-testid="access-ending-soon" style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
          {t.access_ending_soon || 'Access ending soon'}
        </div>
      )}
      {showExtendButton && (
        <a
          href="/extend-access"
          data-testid="request-extension-btn"
          style={{
            display: 'inline-block', marginTop: '8px',
            padding: '5px 14px', fontSize: '11px', letterSpacing: '0.04em',
            border: '1px solid var(--kahla-border)', color: 'var(--kahla-text)',
            textDecoration: 'none', fontFamily: 'inherit',
          }}
        >{t.request_extension || 'Request extension'}</a>
      )}
    </div>
  );
};

// SettingsWelcomeField was extracted to
// /app/frontend/src/components/SettingsWelcomeField.js and imported above.

export default UserSettings;
